import _, { get } from "lodash";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import { useSelector } from "react-redux";
import { Button, Dimmer, Form, Grid, Icon, Label, Loader, Modal, Segment } from "semantic-ui-react";
import { getCrmRequestBulkEdit, postCrmRequestBulkEdit } from "../../queries/crmRequests";
import networkSelectors from "../../selectors/network";
import { ControlledCrmStatusSelect } from "../CrmStatusSelect";
import { ControlledEmployeeSelect } from "../EmployeeSelect";
import { ControlledVirtualDropdown } from "../ControlledForm";
import { getAllCrmTopics } from "../../queries/crmTopicsAll";

interface IBulkEditModal {
  selectedCases: number[];
  setOpen: (open: boolean) => void;
  open: boolean;
  onComplete: () => void;
  maxUpdateableCases?: number;
}

const BulkEditModal = ({
  selectedCases,
  setOpen,
  open,
  onComplete,
  maxUpdateableCases = 100, // max number of cases that can be updated at once
}: IBulkEditModal) => {
  const { control, handleSubmit, watch } = useForm();
  const statusWatch = watch("status", "");
  const networkHeaders = useSelector(networkSelectors.defaultHeaders);
  const [mutate] = useMutation(postCrmRequestBulkEdit(networkHeaders), {
    onSuccess: (data) => {
      const jobid = _.get(data, ["data", "id"], "");
      setWorkingJobId(jobid);
    },
  });
  const [loaderMessage, setLoaderMessage] = useState("");
  const [workingJobId, setWorkingJobId] = useState("");
  const [updatingJobStatus, setUpdatingJobStatus] = useState(false);
  const [showCloseReasonError, setShowCloseReasonError] = useState(false);
  const statusIsClosed = useMemo(() => statusWatch.toString().toLowerCase().includes("close"), [statusWatch]);

  const allCrmTopicsQuery = useQuery<any, any>(["allCrmTopics"], getAllCrmTopics(networkHeaders));
  const allCrmTopicsCount = get<any[]>(allCrmTopicsQuery.data, "data", []).length;
  const crmTopicOptions = useMemo(() => {
    // First step, turn flat list of topics into a map of groups as keys and topics as values
    const groups = get<any[]>(allCrmTopicsQuery.data, "data", []).reduce<Map<string, any[]>>(
      (groups, { attributes, id }: any) => {
        const groupName = attributes.groupName;
        const groupNameAlreadyExists = groups.has(groupName);
        if (groupNameAlreadyExists) groups.get(groupName)?.push({ id, attributes });
        if (!groupNameAlreadyExists) groups.set(groupName, [{ id, attributes }]);
        return groups;
      },
      new Map(),
    );
    // Second step, transform further into options for the virtual dropdown
    const crmTopicOptions: any[] = [];
    groups.forEach((topics, groupName) => {
      crmTopicOptions.push({
        label: groupName,
        options: topics.map((topicData) => ({ label: topicData.attributes.classificationText, value: topicData.id })),
      });
    });
    return crmTopicOptions;
  }, [allCrmTopicsQuery.data]);

  useInterval(() => {
    if (!open) return;
    if (!workingJobId) return;
    if (updatingJobStatus) return;

    setUpdatingJobStatus(true);

    console.log("Starting to poll at " + Date.now());
    getCrmRequestBulkEdit(networkHeaders)({ ids: [workingJobId] }).then((data) => {
      const status = _.get(data, ["data", "0", "attributes", "status"]);
      const message = _.get(data, ["data", "0", "attributes", "message"]);
      if (status == "pending") {
        setLoaderMessage("Changes are pending ...");
      } else if (status == "running" && message) {
        setLoaderMessage(message);
      } else if (status == "complete") {
        setWorkingJobId("");
        setOpen(false);
        onComplete();
        setLoaderMessage("");
      }
      setUpdatingJobStatus(false);
    });
  }, 5000);

  function onSubmit(data: any) {
    setShowCloseReasonError(false);

    const attributes: any = {};
    if (data["topic"]) {
      attributes.topicId = data["topic"]["value"];
    }
    if (data["assigneeId"] && !statusIsClosed) {
      attributes.assignedToId = data["assigneeId"];
    }
    if (data["status"]) {
      attributes.status = data["status"];
    }
    if (data["reasonClosed"] && statusIsClosed) {
      attributes.reasonClosed = data["reasonClosed"];
    } else if (statusIsClosed && !data["reasonClosed"]) {
      setShowCloseReasonError(true);
      return;
    }

    const payload = {
      ids: selectedCases.slice(0, maxUpdateableCases),
      attributes: attributes,
      commentToCitizen: data["commentToCitizen"],
    };
    setLoaderMessage("Submitting bulk edit ...");
    mutate(payload);
  }

  const exceedsMaxUpdateableCases = selectedCases.length > maxUpdateableCases;
  const header = `Update ${!exceedsMaxUpdateableCases ? selectedCases.length : maxUpdateableCases} Requests`;

  return (
    <Modal as={Form} onClose={() => setOpen(false)} onOpen={() => setOpen(true)} open={open} closeOnDimmerClick={false}>
      <Modal.Header>
        <div className='tw-flex tw-gap-2'>
          {header}
          {exceedsMaxUpdateableCases && (
            <Label circular className='warning-label'>
              <Icon name='warning sign' />
              {selectedCases.length} records were selected but only the first {maxUpdateableCases} will be updated
            </Label>
          )}
        </div>
      </Modal.Header>
      <Modal.Content>
        <Segment basic>
          {loaderMessage != "" && (
            <Dimmer active>
              <Loader indeterminate>{loaderMessage}</Loader>
            </Dimmer>
          )}
          <Grid>
            <Grid.Row>
              <Grid.Column width={8}>
                <Form.Field>
                  <label htmlFor='assignee'>Change Assignee</label>
                  <ControlledEmployeeSelect
                    id='assignee'
                    control={control}
                    name='assigneeId'
                    defaultValue={0}
                    disabled={statusIsClosed}
                  />
                </Form.Field>
              </Grid.Column>
              <Grid.Column width={8}>
                <Form.Field>
                  <label htmlFor='status'>Change Status</label>
                  <ControlledCrmStatusSelect id='status' control={control} name='status' defaultValue={null} />
                </Form.Field>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column width={8}>
                <Form.Field>
                  <label>Change Topic</label>
                  <ControlledVirtualDropdown
                    control={control}
                    name='topic'
                    placeholder='Topic'
                    options={crmTopicOptions}
                    useVirtualList={allCrmTopicsCount >= 100}
                    searchByGroup={allCrmTopicsCount <= 500}
                  />
                </Form.Field>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column>
                {!statusIsClosed && (
                  <Controller
                    control={control}
                    name='commentToCitizen'
                    render={({ onChange }) => (
                      <Form.TextArea
                        label='Send comment to customer'
                        placeholder=''
                        onChange={(e: any, { value }: any) => onChange(value)}
                      />
                    )}
                  />
                )}
                {statusIsClosed && (
                  <Controller
                    control={control}
                    name='reasonClosed'
                    render={({ onChange }) => (
                      <Form.TextArea
                        required
                        label='Add Reason Closed'
                        placeholder=''
                        onChange={(e: any, { value }: any) => onChange(value)}
                        error={showCloseReasonError && { content: "Please enter a closed reason", pointing: "below" }}
                      />
                    )}
                  />
                )}
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Segment>
      </Modal.Content>
      <Modal.Actions>
        <Button content={loaderMessage == "" ? "Cancel" : "Close"} color='red' onClick={() => setOpen(false)} />
        <Button
          content={loaderMessage == "" ? "Submit" : "Working ..."}
          onClick={handleSubmit(onSubmit)}
          primary
          disabled={loaderMessage == "" ? false : true}
        />
      </Modal.Actions>
    </Modal>
  );
};

// https://blog.bitsrc.io/polling-in-react-using-the-useinterval-custom-hook-e2bcefda4197
function useInterval(callback: () => void, delay: number) {
  const savedCallback = useRef<() => void>();

  // Remember the latest calllback
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval
  useEffect(() => {
    function tick() {
      if (savedCallback.current) {
        savedCallback.current();
      }
    }
    if (delay !== null) {
      const id = setInterval(tick, delay);
      return () => {
        clearInterval(id);
      };
    }
  }, [callback, delay]);
}

export default BulkEditModal;
